import axios from 'axios';

const API_URL = '/api/posts';

const authHeaders = (token?: string | null) => token ? { Authorization: `Bearer ${token}` } : undefined;
export interface PostPage<T = any> {
    content: T[];
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
}

export const normalizePosts = (data: any) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.content)) {
        return data.content;
    }

    if (Array.isArray(data?.posts)) {
        return data.posts;
    }

    return [];
};

export const normalizePostPage = (data: any): PostPage => {
    if (Array.isArray(data?.content)) {
        return {
            content: data.content,
            number: data.number ?? 0,
            size: data.size ?? data.content.length,
            totalElements: data.totalElements ?? data.content.length,
            totalPages: data.totalPages ?? 1,
            first: data.first ?? true,
            last: data.last ?? true,
        };
    }

    const content = normalizePosts(data);
    return {
        content,
        number: 0,
        size: content.length,
        totalElements: content.length,
        totalPages: content.length > 0 ? 1 : 0,
        first: true,
        last: true,
    };
};

export const getPosts = async (page: number = 0, size: number = 1, token?: string | null) => {
  const response = await axios.get(`${API_URL}?page=${page}&size=${size}`, { headers: authHeaders(token) });
  return normalizePosts(response.data);
};

export const getPostPage = async (page: number = 0, size: number = 1, token?: string | null) => {
  const response = await axios.get(`${API_URL}?page=${page}&size=${size}`, { headers: authHeaders(token) });
  return normalizePostPage(response.data);
};

export const getPost = async (postId: number | string, token?: string | null) => {
    const response = await axios.get(`${API_URL}/${postId}`, { headers: authHeaders(token) });
    return response.data;
};

export interface PostShareInfo {
    id: number;
    title: string;
    text: string;
    url: string;
    imageUrl?: string;
}

export const getPostShareInfo = async (postId: number | string, token?: string | null) => {
    const response = await axios.get(`${API_URL}/${postId}/share`, { headers: authHeaders(token) });
    return response.data as PostShareInfo;
};

export const getPopularPosts = async (limit: number = 5, token?: string | null) => {
    const response = await axios.get(`${API_URL}/popular`, { params: { limit }, headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export const searchPosts = async (keyword: string, categoryId?: number, token?: string | null) => {
    const params = new URLSearchParams();
    params.append('keyword', keyword);
    if (categoryId) params.append('categoryId', categoryId.toString());
    
    const response = await axios.get(`${API_URL}/search`, { params, headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export const searchUserQueries = async (
    keyword: string,
    categoryId: number | undefined,
    source: 'desktop' | 'mobile',
    token?: string | null,
) => {
    const response = await axios.post('/api/search/queries', {
        keyword,
        categoryId: categoryId ?? null,
        source,
    }, { headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export const getPostsByTag = async (tagName: string, token?: string | null) => {
    const response = await axios.get(`${API_URL}/tag`, { params: { name: tagName }, headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export const getActiveNotices = async (token?: string | null) => {
    const response = await axios.get(`${API_URL}/notices/active`, { params: { _ts: Date.now() }, headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export interface ArchiveDay {
    date: string;
    postCount: number;
}

export const getArchiveDays = async (year: number, month: number, token?: string | null) => {
    const response = await axios.get(`${API_URL}/archive/days`, {
        params: { year, month, _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', ...authHeaders(token) },
    });
    return response.data as ArchiveDay[];
};

export const getPostsByDate = async (date: string, token?: string | null) => {
    const response = await axios.get(`${API_URL}/archive`, { params: { date }, headers: authHeaders(token) });
    return normalizePosts(response.data);
};

export const createPost = async (postData: any, token: string) => {
    const response = await axios.post(API_URL, postData, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};

export const updatePost = async (postId: number | string, postData: any, token: string) => {
    const response = await axios.put(`${API_URL}/${postId}`, postData, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};

export const deletePost = async (postId: number | string, token: string) => {
    await axios.delete(`${API_URL}/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getAttachmentDownloadUrl = (postId: number, attachmentId: number) => {
    return `${API_URL}/${postId}/attachments/${attachmentId}/download`;
};

export const downloadAttachment = async (postId: number, attachmentId: number, filename: string, token?: string | null) => {
    const response = await axios.get(getAttachmentDownloadUrl(postId, attachmentId), {
        headers: authHeaders(token),
        responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export interface PostLikeResponse {
    post: {
        likeCount: number;
    };
    liked: boolean;
}

export const addPostLike = async (postId: number) => {
    const response = await axios.post(`${API_URL}/${postId}/likes`);
    return response.data as PostLikeResponse;
};

export const incrementPostView = async (postId: number | string) => {
    const response = await axios.post(`${API_URL}/${postId}/views`);
    return response.data;
};
